import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  LabelList,
  Line,
} from 'recharts';
import useAdminDashboardParetoChart from './AdminDashboardParetoChart.hook';
import {
  chartWrapper,
  customTooltip, customTooltipCumulative,
  customTooltipLabel,
  customTooltipValue,
  loaderWrapper,
} from './AdminDashboardParetoChart.style';
import { AdminDashboardParetoChartDataPropsType } from './AdminDashboardParetoChart.types';
import Box from 'yoda-ui/components/Box';
import Loader from 'yoda-ui/components/Loader';
import { YodaColors, YodaFontSize } from 'yoda-ui/yodaTheme';

const AdminDashboardParetoChart: FC<AdminDashboardParetoChartDataPropsType> = ({
  paretoData,
  loading = false,
}) => {
  const { t } = useTranslation();
  const { tooltipContent, renderLabel } = useAdminDashboardParetoChart();

  return (
    <Box sx={ chartWrapper }>
      {
        loading
          ? <Box sx={ loaderWrapper }><Loader center/></Box>
          : paretoData.length === 0
            ? <Box sx={ loaderWrapper }>{ t('maya_admin_dashboard_charts_no_data_title') }</Box>
            : <ResponsiveContainer>
              <ComposedChart data={ paretoData } margin={ { top: 50 } }>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="categoryName"
                  interval={ 0 }
                  height={ 80 }
                  tickMargin={ 10 }
                  tick={
                    (props) => {
                      const { x, y, payload } = props;
                      const words = String(payload.value).split(' ');
                      return (
                        <text x={ x } y={ y } textAnchor="middle" fill={ YodaColors.black } fontSize={ YodaFontSize.xxSmall }>
                          {
                            words.map((word: string, index: number) => (
                              <tspan key={ index } x={ x } dy={ index === 0 ? 0 : 14 }>
                                { word }
                              </tspan>
                            ))
                          }
                        </text>
                      );
                    }
                  }
                />
                <YAxis yAxisId="left" />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={ [0, 100] }
                  tickFormatter={ (tick) => `${tick}%` }
                />
                <Tooltip
                  content={
                    (props) => {
                      const labelData = tooltipContent(props);
                      if (!labelData) return null;

                      return (
                        <Box sx={ customTooltip }>
                          <Box sx={ customTooltipLabel }>{ labelData.label }</Box>
                          {
                            labelData.valueItem && <Box sx={ customTooltipValue }>
                              { t('maya_admin_dashboard_pareto_chart_value_title') } { labelData.valueItem.value }</Box>
                          }
                          {
                            labelData.cumulativeItem && <Box sx={ customTooltipCumulative }>
                              { t('maya_admin_dashboard_pareto_chart_cumulative_title') } { labelData.cumulativeItem.value }%</Box>
                          }
                        </Box>
                      );
                    }
                  }
                />
                <Bar yAxisId="left" dataKey="count" fill={ YodaColors.primaryBlue }>
                  <LabelList
                    dataKey="count"
                    position="bottom"
                    offset={ -40 }
                    content={
                      (props) => {
                        const labelData = renderLabel(props);
                        if (!labelData) return null;

                        return (
                          <text
                            x={ labelData.x }
                            y={ labelData.y }
                            fill={ labelData.fillColor }
                            fontSize={ YodaFontSize.medium }
                            fontWeight="bold"
                            textAnchor="middle"
                            dominantBaseline="middle"
                          >
                            { labelData.value }
                          </text>
                        );
                      }
                    }
                  />
                </Bar>
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cumulativePercent"
                  stroke="#FF0000"
                  strokeWidth={ 2 }
                  dot={ { r: 3, fill: YodaColors.red } }
                >
                  <LabelList
                    position="top"
                    offset={ 10 }
                    content={
                      (props) => {
                        const { value, x, y } = props;
                        return (
                          <text
                            x={ x }
                            y={ y }
                            dy={ -10 }
                            fill={ YodaColors.background }
                            fontSize={ YodaFontSize.medium }
                            textAnchor="middle"
                          >
                            { value }%
                          </text>
                        );
                      }
                    }
                  />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
      }
    </Box>
  );
};

export default AdminDashboardParetoChart;
